import { riskBandKey } from "@/domain/quant/risk-bands";
import { parseFiniteNumber } from "@/domain/quant/number-parsing";
import { classTagsOf } from "@/domain/quant/class-mix";
import type { AssetFacts } from "@/domain/quant/asset-facts";
import type { ClientHolding, ClientPortfolio } from "@/domain/clients/types";
import type { CurrentPortfolio, PortfolioHoldingSnapshot } from "@/domain/quant/types";

export interface QuantPortfolioWireHolding {
  id?: string | null;
  isin?: string | null;
  quantity?: string | number | null;
  value?: string | number | null;
  portfolio_currency_value?: string | number | null;
  weight?: string | number | null;
  quant_asset_id?: string | null;
  asset_quant_name?: string | null;
  pending?: boolean | null;
  asset_classes?: Array<{
    id?: string | null;
    name?: string | null;
    quant_asset_class_order_id?: string | number | null;
  }> | null;
  asset_name?: string | null;
  current_value?: string | number | null;
  current_weight?: string | number | null;
  asset_class_name?: string | null;
  asset_type?: string | number | null;
  is_cash?: boolean | null;
  is_fixed?: boolean | null;
  fee?: string | number | null;
  asset_id?: string | number | null;
}

interface QuantPortfolioWireLegacyBody {
  name?: string | null;
  current_value?: string | number | null;
  current_mean_return?: string | number | null;
  current_volatility?: string | number | null;
  current_cvar?: string | number | null;
  items?: QuantPortfolioWireHolding[] | null;
}

export interface QuantPortfolioWire {
  id?: string | null;
  name?: string | null;
  sync_external_portfolio_id?: string | null;
  current_mean_return_rate?: string | number | null;
  current_volatility?: string | number | null;
  current_cvar_rate?: string | number | null;
  advisory_fee_rate?: string | number | null;
  deposit_fee_rate?: string | number | null;
  current_value?: string | number | null;
  quant_currency_id?: string | number | null;
  quant_asset_class_map_id?: string | number | null;
  portfolio_items?: QuantPortfolioWireHolding[] | null;
  portfolio_constraints?: unknown[] | null;
  portfolio_item_constraints?: unknown[] | null;
  quant_portfolio?: QuantPortfolioWireLegacyBody | null;
}

export type AssetClassResolver = (
  mapId: string | null,
  assetId: string | null,
  order: string,
) => string | null;

export function toPortfolio(
  item: QuantPortfolioWire,
  classes?: AssetClassResolver,
): ClientPortfolio | null {
  const parts = unwrap(item);
  if (!parts) return null;
  const { id, legacy, rawItems } = parts;
  const mapId =
    item.quant_asset_class_map_id != null ? String(item.quant_asset_class_map_id) : null;
  const holdings = rawItems.map((holding, index) => toHolding(holding, id, index, mapId, classes));
  const value = portfolioValue(item, legacy);
  const stats = currentStats(id, item, legacy, rawItems);
  const volatilityPct = pctOrNull(stats.volatility);
  return {
    id,
    name: portfolioName(item),
    value,
    returnPct: pctOrNull(stats.meanReturn),
    volatilityPct,
    cvarPct: pctOrNull(stats.cvar),
    riskBandKey: volatilityPct === null ? null : riskBandKey(volatilityPct),
    holdings,
  };
}

export function portfolioName(item: QuantPortfolioWire): string {
  const legacy = item.quant_portfolio ?? null;
  return (stringOrNull(item.name) ?? stringOrNull(legacy?.name) ?? "").trim() || "Portfölj";
}

export function toCurrentPortfolio(
  item: QuantPortfolioWire,
  facts: AssetFacts,
): CurrentPortfolio | null {
  const parts = unwrap(item);
  if (!parts) return null;
  const { id, legacy, rawItems } = parts;
  const holdings: PortfolioHoldingSnapshot[] = rawItems.map((holding) => ({
    isin: toIsin(holding, facts),
    assetName: stringOrNull(holding.asset_quant_name) ?? stringOrNull(holding.asset_name),
    assetId: parseFiniteNumber(assetIdOf(holding)),
    value: toSekValue(holding),
    weight: parseFiniteNumber(holding.weight) ?? parseFiniteNumber(holding.current_weight),
    fee: toFeePercent(holding, facts),
    classes: classTagsOf(holding.asset_classes),
  }));
  const stats = currentStats(id, item, legacy, rawItems);
  return {
    holdings,
    volatility: stats.volatility,
    meanReturn: stats.meanReturn,
    cvar: stats.cvar,
    value: portfolioValue(item, legacy),
  };
}

export function aggregate(portfolios: ClientPortfolio[]): {
  totalValue: number | null;
  returnPct: number | null;
  volatilityPct: number | null;
  cvarPct: number | null;
} {
  const totalValue = portfolios.some((portfolio) => portfolio.value === null)
    ? null
    : portfolios.reduce((sum, portfolio) => sum + (portfolio.value as number), 0);

  const weighted = (selectMetric: (portfolio: ClientPortfolio) => number | null): number | null => {
    const withMetric = portfolios.filter(
      (portfolio) => selectMetric(portfolio) !== null && portfolio.value !== 0,
    );
    if (withMetric.length === 0) return null;
    const withValue = withMetric.filter((portfolio) => portfolio.value !== null);
    const base = withValue.reduce((sum, portfolio) => sum + (portfolio.value as number), 0);
    return base > 0
      ? withValue.reduce(
          (sum, portfolio) =>
            sum + (selectMetric(portfolio) as number) * ((portfolio.value as number) / base),
          0,
        )
      : withMetric.reduce((sum, portfolio) => sum + (selectMetric(portfolio) as number), 0) /
          withMetric.length;
  };

  return {
    totalValue,
    returnPct: weighted((portfolio) => portfolio.returnPct),
    volatilityPct: weighted((portfolio) => portfolio.volatilityPct),
    cvarPct: weighted((portfolio) => portfolio.cvarPct),
  };
}

function toSekValue(holding: QuantPortfolioWireHolding): number | null {
  return parseFiniteNumber(holding.portfolio_currency_value);
}

function toIsin(holding: QuantPortfolioWireHolding, facts: AssetFacts): string | null {
  return stringOrNull(holding.isin) ?? facts.isin(assetIdOf(holding));
}

function toFeePercent(holding: QuantPortfolioWireHolding, facts: AssetFacts): number | null {
  return facts.feePercent(assetIdOf(holding));
}

export function toAdvisoryFeePercent(item: QuantPortfolioWire): number | null {
  const rate = parseFiniteNumber(item.advisory_fee_rate);
  return rate === null ? null : rate * 100;
}

export function toDepositFeePercent(item: QuantPortfolioWire): number | null {
  const rate = parseFiniteNumber(item.deposit_fee_rate);
  return rate === null ? null : rate * 100;
}

export function assetClassNameByOrder(
  holding: Pick<QuantPortfolioWireHolding, "asset_classes">,
  order: string,
): string | null {
  const entry = (holding.asset_classes ?? []).find(
    (assetClass) => String(assetClass?.quant_asset_class_order_id ?? "") === order,
  );
  const name = stringOrNull(entry?.name)?.trim();
  return name || null;
}

function unwrap(
  item: QuantPortfolioWire,
  fallbackId?: string,
): {
  id: string;
  legacy: QuantPortfolioWireLegacyBody | null;
  rawItems: QuantPortfolioWireHolding[];
} | null {
  const id = typeof item?.id === "string" && item.id !== "" ? item.id : (fallbackId ?? null);
  if (!id) {
    warnReject("(no id)", "missing portfolio id");
    return null;
  }
  const legacy = item.quant_portfolio ?? null;
  const rawItems = Array.isArray(item.portfolio_items)
    ? item.portfolio_items
    : Array.isArray(legacy?.items)
      ? legacy.items
      : null;
  if (!rawItems) {
    warnReject(id, "portfolio_items is missing or not an array");
    return null;
  }
  return { id, legacy, rawItems };
}

function warnReject(portfolioId: string, reason: string): void {
  console.warn(`[portfolio-mapping] rejected portfolio ${portfolioId}: ${reason}`);
}

function toHolding(
  holding: QuantPortfolioWireHolding,
  portfolioId: string,
  index: number,
  mapId: string | null,
  classes?: AssetClassResolver,
): ClientHolding {
  const instrument =
    (stringOrNull(holding.asset_quant_name) ?? stringOrNull(holding.asset_name) ?? "").trim() ||
    "—";
  const joinedName = (order: string): string | null => {
    const assetId = assetIdOf(holding);
    return classes?.(mapId, assetId === null ? null : String(assetId), order) ?? null;
  };
  const assetClassName =
    assetClassNameByOrder(holding, "2") ??
    joinedName("2") ??
    ((
      stringOrNull(holding.asset_classes?.[0]?.name) ??
      stringOrNull(holding.asset_class_name) ??
      ""
    ).trim() ||
      null);
  return {
    id: `${portfolioId}:${holding.isin ?? holding.id ?? index}`,
    instrument,
    assetClassName,
    quantity: parseFiniteNumber(holding.quantity) ?? 0,
    value: toSekValue(holding),
    weightPct: null,
  };
}

function portfolioValue(
  item: QuantPortfolioWire,
  legacy: QuantPortfolioWireLegacyBody | null,
): number | null {
  return parseFiniteNumber(item.current_value) ?? parseFiniteNumber(legacy?.current_value);
}

function currentStats(
  id: string,
  item: QuantPortfolioWire,
  legacy: QuantPortfolioWireLegacyBody | null,
  rawItems: QuantPortfolioWireHolding[],
): { meanReturn: number | null; volatility: number | null; cvar: number | null } {
  const meanReturn =
    parseFiniteNumber(item.current_mean_return_rate) ??
    parseFiniteNumber(legacy?.current_mean_return);
  const volatility =
    parseFiniteNumber(item.current_volatility) ?? parseFiniteNumber(legacy?.current_volatility);
  const cvar = parseFiniteNumber(item.current_cvar_rate) ?? parseFiniteNumber(legacy?.current_cvar);
  const funded = rawItems.some(
    (holding) =>
      (parseFiniteNumber(holding.value) ?? parseFiniteNumber(holding.current_value) ?? 0) > 0,
  );
  if (meanReturn !== 0 || volatility !== 0 || cvar !== 0) return { meanReturn, volatility, cvar };
  if (funded) {
    console.warn(
      `[portfolio-mapping] portfolio ${id}: mean return, volatility and CVaR ` +
        `are ALL exactly 0 on a funded portfolio (${rawItems.length} holdings) — ` +
        `the degenerate-stats fingerprint (quant fault measured 2026-08-20; a ` +
        `fresh analysis recomputes the same zeros). Treating the statistics as ` +
        `not reported: the UI renders "—" and no risk band, never a confident ` +
        `0,0 %/Låg.`,
    );
  }
  return { meanReturn: null, volatility: null, cvar: null };
}

function assetIdOf(holding: QuantPortfolioWireHolding): string | number | null {
  return holding.quant_asset_id ?? holding.asset_id ?? null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function pctOrNull(frac: number | null): number | null {
  return typeof frac === "number" ? frac * 100 : null;
}
